import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useParams, useNavigate } from "react-router-dom";
import SidebarComponent from "../SidbarComponent";
import TopBarComponent from "../TopBarComponent";
import { FooterComponents } from "../FooterComponent";
import { Award, Download, Search, ArrowLeft, User, GraduationCap, TrendingUp, Users, CheckCircle } from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { getToken } from "../../utils/auth";

export default function ApplicationEvaluationScores() {
  const { programId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [evaluationDetails, setEvaluationDetails] = useState([]);
  const [filteredDetails, setFilteredDetails] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20); // Show 20 items per page

  useEffect(() => {
    fetchEvaluationDetails();
  }, [programId]);

  useEffect(() => {
    filterDetails();
  }, [searchTerm, evaluationDetails]);

  const fetchEvaluationDetails = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}hec/applicationEvaluationScore/evaluation-details`,
        {
          params: { programId: programId },
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );
      setEvaluationDetails(response.data || []);
      setFilteredDetails(response.data || []);
    } catch (err) {
      setError("Failed to fetch evaluation details");
      toast.error("Failed to load evaluation details");
    } finally {
      setLoading(false);
    }
  };

  const filterDetails = () => {
    if (!searchTerm) {
      setFilteredDetails(evaluationDetails);
      return;
    }

    const filtered = evaluationDetails.filter(
      (detail) =>
        detail.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        detail.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        detail.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        detail.nationalId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        detail.priorityCourse?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    setFilteredDetails(filtered);
  };

  const handleExportToExcel = () => {
    const excelData = filteredDetails.map((detail, index) => ({
      "Rank": index + 1,
      "First Name": detail.firstName || "",
      "Last Name": detail.lastName || "",
      "Email": detail.email || "",
      "National ID": detail.nationalId || "",
      "Phone": detail.phoneNumber || "",
      "Gender": detail.gender || "",
      "Current Level": detail.currentLevel || "",
      "Apply Level": detail.applyLevel || "",
      "Field of Study": detail.fieldOfStudy || "",
      "Priority Course": detail.priorityCourse || "",
      "Priority Level": detail.priorityLevel || "",
      "Academic Merit": detail.academicMerit || "",
      "Working Place": detail.workingPlace || "",
      "Start Date": detail.startDate || "",
      "End Date": detail.endDate || "",
      "Tuition Fee": detail.tuitionFee || "",
      "Monthly Living Allowance": detail.monthlyLivingAllowance || "",
      "Research Allowance": detail.researchAllowance || "",
      "Air Ticket Allowance": detail.airTicketAllowance || "",
      "Cumulative Average": detail.cumulativeAverage?.toFixed(2) || "",
      "Total Weighted Score": detail.totalWeightedScore?.toFixed(2) || "",
      "Number of Evaluators": detail.numberOfEvaluators || "",
      "Evaluation Status": detail.evaluationStatus || "",
      "Finalized Date": detail.finalizedDate ? new Date(detail.finalizedDate).toLocaleString() : "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Evaluation Scores");

    // Set column widths
    worksheet["!cols"] = [
      { wch: 6 },  // Rank
      { wch: 15 }, // First Name
      { wch: 15 }, // Last Name
      { wch: 25 }, // Email
      { wch: 15 }, // National ID
      { wch: 15 }, // Phone
      { wch: 10 }, // Gender
      { wch: 15 }, // Current Level
      { wch: 15 }, // Apply Level
      { wch: 20 }, // Field of Study
      { wch: 30 }, // Priority Course
      { wch: 15 }, // Priority Level
      { wch: 15 }, // Academic Merit
      { wch: 20 }, // Working Place
      { wch: 15 }, // Start Date
      { wch: 15 }, // End Date
      { wch: 15 }, // Tuition Fee
      { wch: 20 }, // Monthly Living
      { wch: 18 }, // Research
      { wch: 18 }, // Air Ticket
      { wch: 18 }, // Cumulative Avg
      { wch: 18 }, // Total Weighted
      { wch: 18 }, // Num Evaluators
      { wch: 18 }, // Status
      { wch: 20 }, // Finalized Date
    ];

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, `Evaluation_Scores_${new Date().toISOString().split("T")[0]}.xlsx`);

    toast.success("Evaluation scores exported successfully!", {
      position: "top-center",
      autoClose: 3000,
      theme: "colored",
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };

  const getAverageScore = () => {
    if (filteredDetails.length === 0) return 0;
    const sum = filteredDetails.reduce((acc, detail) => acc + (detail.cumulativeAverage || 0), 0);
    return (sum / filteredDetails.length).toFixed(2);
  };

  const getTotalTuitionFee = () => {
    const sum = filteredDetails.reduce((acc, detail) => acc + (detail.tuitionFee || 0), 0);
    return sum.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getTotalMonthlyLivingAllowance = () => {
    const sum = filteredDetails.reduce((acc, detail) => acc + (detail.monthlyLivingAllowance || 0), 0);
    return sum.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getTotalResearchAllowance = () => {
    const sum = filteredDetails.reduce((acc, detail) => acc + (detail.researchAllowance || 0), 0);
    return sum.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getTotalAirTicketAllowance = () => {
    const sum = filteredDetails.reduce((acc, detail) => acc + (detail.airTicketAllowance || 0), 0);
    return sum.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getStatusBadge = (status) => {
    const baseClasses = "px-3 py-1 rounded-full text-xs font-medium";
    switch (status) {
      case "COMPLETED":
        return `${baseClasses} bg-green-100 text-green-800`;
      case "IN_PROGRESS":
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case "PENDING":
        return `${baseClasses} bg-gray-100 text-gray-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  // Pagination calculations
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredDetails.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredDetails.length / itemsPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const renderPaginationButtons = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <button
          key={i}
          onClick={() => paginate(i)}
          className={`mx-1 px-3 py-1 rounded-lg text-sm font-medium ${
            currentPage === i
              ? "bg-hec-700 text-white"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          {i}
        </button>
      );
    }
    return pageNumbers;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TopBarComponent />
        <aside className="app-legacy-aside transition-transform -translate-x-full sm:translate-x-0 bg-white border-r border-gray-200">
          <SidebarComponent />
        </aside>
        <div className="pt-6 sm:ml-64">
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <div className="w-16 h-16 bg-hec-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                <Award className="w-8 h-8 text-hec-600" />
              </div>
              <p className="text-hec-600 text-lg">Loading evaluation scores...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TopBarComponent />
        <aside className="app-legacy-aside transition-transform -translate-x-full sm:translate-x-0 bg-white border-r border-gray-200">
          <SidebarComponent />
        </aside>
        <div className="pt-6 sm:ml-64">
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <Award className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <p className="text-red-600 text-lg">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TopBarComponent />
      <aside className="app-legacy-aside transition-transform -translate-x-full sm:translate-x-0 bg-white border-r border-gray-200">
        <SidebarComponent />
      </aside>

      <div className="pt-6 sm:ml-64">
        <div className="p-6 min-h-screen">
          <div className="py-8 mt-10">
            {/* Header */}
            <div className="mb-8">
              <div className="bg-white rounded-lg shadow-sm border border-hec-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() => navigate(-1)}
                      className="w-12 h-12 bg-hec-100 hover:bg-hec-200 rounded-lg flex items-center justify-center transition-colors"
                    >
                      <ArrowLeft className="w-6 h-6 text-hec-700" />
                    </button>
                    <div className="w-14 h-14 bg-gradient-to-r from-hec-600 to-hec-700 rounded-2xl flex items-center justify-center shadow-lg">
                      <Award className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h1 className="text-3xl font-bold text-hec-700">Application Evaluation Scores</h1>
                      <p className="text-hec-600 mt-1">
                        Ranked list of {filteredDetails.length} evaluated application(s)
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleExportToExcel}
                    className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all duration-200 shadow-lg"
                  >
                    <Download className="w-5 h-5" />
                    <span>Export to Excel</span>
                  </button>
                </div>

                {/* Statistics Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-blue-600 font-medium">Total Evaluated</p>
                        <p className="text-3xl font-bold text-blue-700 mt-1">{filteredDetails.length}</p>
                      </div>
                      <CheckCircle className="w-12 h-12 text-blue-500 opacity-50" />
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-green-600 font-medium">Average Score</p>
                        <p className="text-3xl font-bold text-green-700 mt-1">{getAverageScore()}</p>
                      </div>
                      <TrendingUp className="w-12 h-12 text-green-500 opacity-50" />
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-purple-600 font-medium">Highest Score</p>
                        <p className="text-3xl font-bold text-purple-700 mt-1">
                          {filteredDetails.length > 0 ? filteredDetails[0]?.cumulativeAverage?.toFixed(2) : "0"}
                        </p>
                      </div>
                      <Award className="w-12 h-12 text-purple-500 opacity-50" />
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-lg border border-orange-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-orange-600 font-medium">Avg Evaluators</p>
                        <p className="text-3xl font-bold text-orange-700 mt-1">
                          {filteredDetails.length > 0 
                            ? Math.round(filteredDetails.reduce((sum, d) => sum + (d.numberOfEvaluators || 0), 0) / filteredDetails.length)
                            : 0}
                        </p>
                      </div>
                      <Users className="w-12 h-12 text-orange-500 opacity-50" />
                    </div>
                  </div>
                </div>

                {/* Financial Statistics Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
                  <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-4 rounded-lg border border-indigo-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-indigo-600 font-medium">Total Tuition Fee</p>
                        <p className="text-2xl font-bold text-indigo-700 mt-1">${getTotalTuitionFee()}</p>
                      </div>
                      <GraduationCap className="w-12 h-12 text-indigo-500 opacity-50" />
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-teal-50 to-teal-100 p-4 rounded-lg border border-teal-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-teal-600 font-medium">Total Living Allowance</p>
                        <p className="text-2xl font-bold text-teal-700 mt-1">${getTotalMonthlyLivingAllowance()}</p>
                      </div>
                      <Users className="w-12 h-12 text-teal-500 opacity-50" />
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-pink-50 to-pink-100 p-4 rounded-lg border border-pink-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-pink-600 font-medium">Total Research Allowance</p>
                        <p className="text-2xl font-bold text-pink-700 mt-1">${getTotalResearchAllowance()}</p>
                      </div>
                      <Search className="w-12 h-12 text-pink-500 opacity-50" />
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 p-4 rounded-lg border border-cyan-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-cyan-600 font-medium">Total Air Ticket Allowance</p>
                        <p className="text-2xl font-bold text-cyan-700 mt-1">${getTotalAirTicketAllowance()}</p>
                      </div>
                      <TrendingUp className="w-12 h-12 text-cyan-500 opacity-50" />
                    </div>
                  </div>
                </div>

                {/* Search */}
                <div className="mt-6 max-w-md">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Search className="w-4 h-4 inline mr-2" />
                    Search Applications
                  </label>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by name, email, ID, or course..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-hec-500 focus:border-hec-500"
                  />
                </div>
              </div>
            </div>

            {/* Applications Table */}
            {filteredDetails.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-hec-200 p-12">
                <div className="text-center">
                  <Award className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No evaluation scores found.</p>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-hec-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-hec-850">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                          Rank
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                          Applicant
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                          Contact
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                          Academic Info
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                          Course
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">
                          Cumulative Score
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">
                          Total Weighted
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">
                          Evaluators
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {currentItems.map((detail, index) => {
                        const actualIndex = indexOfFirstItem + index;
                        return (
                        <tr key={detail.applicationId} className="hover:bg-gray-50">
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                                actualIndex === 0 ? 'bg-yellow-100 text-yellow-700' :
                                actualIndex === 1 ? 'bg-gray-200 text-gray-700' :
                                actualIndex === 2 ? 'bg-orange-100 text-orange-700' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                                {actualIndex + 1}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-hec-100 rounded-full flex items-center justify-center mr-3">
                                <User className="w-5 h-5 text-hec-600" />
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {detail.firstName} {detail.lastName}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {detail.nationalId}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="text-sm text-gray-900">{detail.email}</div>
                            <div className="text-xs text-gray-500">{detail.phoneNumber}</div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="text-sm text-gray-900">
                              {detail.currentLevel} → {detail.applyLevel}
                            </div>
                            <div className="text-xs text-gray-500">
                              Merit: {detail.academicMerit || "N/A"}
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="text-sm font-medium text-gray-900">
                              {detail.priorityCourse || "N/A"}
                            </div>
                            <div className="text-xs text-gray-500">
                              {detail.fieldOfStudy || "N/A"}
                            </div>
                            <div className="mt-1">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                detail.priorityLevel === "HIGH" ? "bg-red-100 text-red-800" :
                                detail.priorityLevel === "MEDIUM" ? "bg-yellow-100 text-yellow-800" :
                                "bg-green-100 text-green-800"
                              }`}>
                                {detail.priorityLevel || "N/A"}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-center">
                            <div className="text-lg font-bold text-hec-700">
                              {detail.cumulativeAverage?.toFixed(2) || "0.00"}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-center">
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-blue-100 text-blue-700">
                              {detail.totalWeightedScore?.toFixed(2) || "0.00"}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-center">
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-purple-100 text-purple-700">
                              {detail.numberOfEvaluators || 0}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-center">
                            <span className={getStatusBadge(detail.evaluationStatus)}>
                              {detail.evaluationStatus || "PENDING"}
                            </span>
                          </td>
                        </tr>
                      )})}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex justify-between items-center px-6 py-4 border-t border-gray-200">
                    <div className="text-sm text-gray-700">
                      Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredDetails.length)} of {filteredDetails.length} results
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                      >
                        Previous
                      </button>
                      {renderPaginationButtons()}
                      <button
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <FooterComponents />
      </div>
    </div>
  );
}
